import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '../../store';
import axios from 'axios';
import * as fromAuth from './../../auth/state';
import get from 'lodash.get';
import { getAccessToken } from './../../auth/state';
import { Note, User } from '../../../interfaces/user';
import { UserSort } from '../../../interfaces/user-sort';

const BASE_API = process.env['REACT_APP_HUM_ADMIN_API_URL'] ?? ' ';
const instance = axios.create({
  baseURL: BASE_API,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

interface Paging {
  page: number;
  pageSize: number;
  startKey?: string;
  sortType?: 'asc' | 'desc';
  sortField?: string;
  search?: string;
}

interface UpdateProfile {
  secondaryEmail?: string;
  group?: string;
  nickname?: string;
}

interface UserTrackerState {
  list: UserSort[];
  paginationKeys: string[];
  isNextPage: boolean;
  isPrevPage: boolean;
  paging: Paging;
  loading: boolean;
  listError: string | null;
  current: User | null;
  currentError: string | null;
  createNoteOpen: boolean;
  createNoteUserId: string | null;
  createNoteError: string | null;
  creatingNewNote: boolean;
}

const initialState: UserTrackerState = {
  list: [],
  paginationKeys: [],
  isNextPage: false,
  isPrevPage: false,
  paging: {
    page: 0,
    pageSize: 30,
    sortType: 'desc',
    sortField: 'paidDate',
    startKey: '',
  },
  loading: false,
  listError: null,
  current: null,
  currentError: null,
  createNoteOpen: false,
  createNoteUserId: null,
  createNoteError: null,
  creatingNewNote: false,
};

export const userTrackerSlice = createSlice({
  name: 'userTracker',
  initialState,
  reducers: {
    startLoading: (state, action: PayloadAction<Paging | undefined>) => {
      if (action.payload) {
        const { page: currentPage } = state.paging;
        const { page: newPage } = action.payload;
        const isNextPage = newPage > currentPage;
        const isPrevPage = newPage < currentPage;
        state.paging = action.payload;
        state.isNextPage = isNextPage;
        state.isPrevPage = isPrevPage;
        if (newPage === 0) {
          state.paginationKeys = [];
        }
        if (isPrevPage) {
          state.paginationKeys.pop();
        }
      }
      state.loading = true;
    },
    loadUsersSuccess: (state, action: PayloadAction<UserSort[]>) => {
      state.loading = false;
      state.list = action.payload;
      const lastItem = action.payload[action.payload.length - 1];
      const nextPageAvailable = action.payload.length === state.paging.pageSize;
      if (
        lastItem &&
        (state.isNextPage || state.paging.page === 0 || (!state.isNextPage && !state.isPrevPage)) &&
        nextPageAvailable
      ) {
        state.paginationKeys.push(lastItem.id);
      }
      state.listError = '';
    },
    loadUsersError: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.listError = action.payload;
    },
    clearCurrent: state => {
      state.current = null;
      state.currentError = '';
      const isLastPage = state.list.length < state.paging.pageSize;
      if (!isLastPage) {
        state.paginationKeys.pop();
      }
    },
    loadUserByIdSuccess: (state, action: PayloadAction<User>) => {
      state.loading = false;
      state.current = action.payload;
      state.currentError = '';
    },
    loadUserByIdError: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.currentError = action.payload;
    },
    openCreateNote: (state, action: PayloadAction<string>) => {
      state.createNoteOpen = true;
      state.createNoteUserId = action.payload;
    },
    closeCreateNote: state => {
      state.createNoteOpen = false;
      state.createNoteUserId = null;
      state.creatingNewNote = false;
      state.createNoteError = '';
    },
    startCreatingNewNote: state => {
      state.creatingNewNote = true;
      state.createNoteError = '';
    },
    createNoteSuccess: (state, action: PayloadAction<Note>) => {
      state.createNoteOpen = false;
      state.creatingNewNote = false;
      state.createNoteError = '';
      if (state.current) {
        (state.current.notes as Note[]).push(action.payload);
      }
    },
    createNoteError: (state, action: PayloadAction<string>) => {
      state.creatingNewNote = false;
      state.createNoteError = action.payload;
    },
    updateUserProfileSuccess: (state, action: PayloadAction<User>) => {},
  },
  extraReducers: {
    [fromAuth.logoutSuccess.type]: state => (state = initialState),
  },
});

export const {
  startLoading,
  loadUsersSuccess,
  loadUsersError,
  clearCurrent,
  loadUserByIdSuccess,
  loadUserByIdError,
  openCreateNote,
  closeCreateNote,
  startCreatingNewNote,
  createNoteSuccess,
  createNoteError,
  updateUserProfileSuccess,
} = userTrackerSlice.actions;

export const loadUsers =
  (paging: Paging): AppThunk =>
  async (dispatch, getState) => {
    const currentPaging = getState().userTracker.paging;
    const pageSizeChanged = paging.pageSize !== currentPaging.pageSize;
    const page = pageSizeChanged ? 0 : paging.page;
    dispatch(startLoading({ ...paging, page }));
    const paginationKeys = getState().userTracker.paginationKeys;
    const startKey =
      paginationKeys.length && page > 0 ? paginationKeys[paginationKeys.length - 1] : '';
    try {
      const accessToken = await getAccessToken();
      if (accessToken) {
        const response = await instance.get<UserSort[]>(`users`, {
          params: {
            ...paging,
            startKey,
          },
          headers: {
            Authorization: `Bearer ${accessToken.getJwtToken()}`,
          },
        });
        dispatch(loadUsersSuccess(response.data));
        return response.data;
      } else {
        dispatch(loadUsersError('Missing token'));
      }
    } catch (e: any) {
      dispatch(loadUsersError(get(e, 'response.data.error', e.message)));
    }
  };

export const loadUserById =
  (id: string): AppThunk =>
  async dispatch => {
    dispatch(startLoading());
    dispatch(clearCurrent());
    try {
      const accessToken = await getAccessToken();
      if (accessToken) {
        const response = await instance.get<User>(`users/${id}`, {
          headers: {
            Authorization: `Bearer ${accessToken.getJwtToken()}`,
          },
        });
        dispatch(loadUserByIdSuccess(response.data));
      } else {
        dispatch(loadUserByIdError('Missing token'));
      }
    } catch (e: any) {
      dispatch(loadUserByIdError(get(e, 'response.data.error', e.message)));
    }
  };

export const createUserNote =
  (text: string): AppThunk =>
  async (dispatch, getState) => {
    dispatch(startCreatingNewNote());
    try {
      const state = getState();
      const accessToken = await getAccessToken();
      if (accessToken) {
        const response = await instance.post<Note>(
          `notes`,
          { text, userId: state.userTracker.createNoteUserId },
          {
            headers: {
              Authorization: `Bearer ${accessToken.getJwtToken()}`,
            },
          }
        );
        dispatch(createNoteSuccess(response.data));
      } else {
        dispatch(createNoteError('Missing token'));
      }
    } catch (e: any) {
      dispatch(createNoteError(get(e, 'response.data.error', e.message)));
    }
  };

export const updateUserProfile =
  (id: string, profile: UpdateProfile): AppThunk =>
  async dispatch => {
    const accessToken = await getAccessToken();
    if (accessToken) {
      const response = await instance.patch<User>(`users/${id}`, profile, {
        headers: {
          Authorization: `Bearer ${accessToken.getJwtToken()}`,
        },
      });
      dispatch(updateUserProfileSuccess(response.data));
    } else {
      throw new Error('Missing token');
    }
  };

export default userTrackerSlice.reducer;
